import { useTheme } from '@daily/shared/contexts/Theme';
import { useResizeObserver } from '@daily/shared/hooks/useResizeObserver';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useCustomIntegrations } from '/hooks/useCustomIntegrations';
import { useScreens } from '/hooks/useScreens';
import { usePinnedId } from '/lib/state/layout';

import { ScreenPinTile } from './ScreenPinTile';
import { SharedTabs } from './SharedTabs';

type Dimensions = {
  height: number;
  width: number;
};

export const ScreensAndPins: React.FC<React.PropsWithChildren<unknown>> =
  () => {
    const { colors } = useTheme();
    const [pinnedId] = usePinnedId();
    const viewRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState<Dimensions>({
      width: 1,
      height: 1,
    });
    const screens = useScreens();
    const { runningMainIntegrations } = useCustomIntegrations();
    const pinnedScreen = useMemo(
      () => screens.find((s) => s.screenId === pinnedId),
      [pinnedId, screens]
    );
    const pinnedIntegration = useMemo(
      () => runningMainIntegrations.find((i) => i.id === pinnedId),
      [runningMainIntegrations, pinnedId]
    );

    useResizeObserver(
      viewRef,
      useCallback((width, height) => {
        setDimensions({
          width,
          height,
        });
      }, [])
    );

    const isPortraitLayout = dimensions.height > dimensions.width;

    const screensCount = screens.length;

    const isPinnedParticipant =
      Boolean(pinnedId) && !pinnedIntegration && !pinnedScreen;

    /**
     * Check if there are any running main integrations.
     * Check if there's a pinned participant.
     */

    /**
     * When there are only 2 running apps or 2 screen shares, we'll render them in 2 separate Tabs components.
     */
    const only2Apps =
      runningMainIntegrations.length === 2 &&
      screensCount === 0 &&
      !isPinnedParticipant;
    const only2Screens =
      runningMainIntegrations.length === 0 &&
      screensCount === 2 &&
      !isPinnedParticipant;

    /**
     * Determine the apps rendered in the first (= primary) section:
     * - When there are no running apps, no apps 😄
     * - When there are exactly 2 apps, render the first
     * - Otherwise render all apps here
     */
    const primaryTabsApps = useMemo(() => {
      if (!runningMainIntegrations.length) return [];
      if (only2Apps) return runningMainIntegrations.slice(0, 1);
      return runningMainIntegrations;
    }, [only2Apps, runningMainIntegrations]);
    /**
     * Determine the screen shares rendered in the first section:
     * - When there's a pinned participant
     * - When there are exactly 2 screen shares, render the first
     * - When there are no apps
     * - Otherwise none (because screens will be rendered in secondary section, when there are apps)
     */
    const primaryTabsScreens = useMemo(() => {
      if (isPinnedParticipant) return screens;
      if (only2Screens) return screens.slice(0, 1);
      if (!primaryTabsApps.length) return screens;
      return [];
    }, [isPinnedParticipant, only2Screens, primaryTabsApps.length, screens]);
    /**
     * There's only 1 case when we render an app in the secondary section:
     * - When there are exactly 2 apps, render the second
     */
    const secondaryTabsApps = useMemo(() => {
      if (only2Apps) return runningMainIntegrations.slice(1, 2);
      return [];
    }, [only2Apps, runningMainIntegrations]);
    /**
     * Screen shares are rendered in secondary section:
     * - When there are exactly 2 screens, render the second here
     * - When there's no pinned participant, but running apps
     * - Otherwise no screens here (because they'll be rendered in the primary section otherwise)
     */
    const secondaryTabsScreens = useMemo(() => {
      if (only2Screens) return screens.slice(1, 2);
      if (!isPinnedParticipant && primaryTabsApps.length) return screens;
      return [];
    }, [isPinnedParticipant, only2Screens, primaryTabsApps.length, screens]);

    /**
     * Determines the relative width and height for each section, based on main area aspect ratio.
     */
    const splitAreaDimensions = useMemo(
      () => ({
        height: isPortraitLayout ? '50%' : '100%',
        width: isPortraitLayout ? '100%' : '50%',
      }),
      [isPortraitLayout]
    );

    return (
      <div
        ref={viewRef}
        className={classNames('screens-pins', {
          portrait: isPortraitLayout,
          'pinned-integration': !!pinnedIntegration,
          'pinned-participant': isPinnedParticipant,
          'pinned-screen': !!pinnedScreen,
        })}
      >
        {primaryTabsApps.length + primaryTabsScreens.length > 0 && (
          <SharedTabs
            className={classNames({
              pinned:
                primaryTabsApps.some((app) => app.id === pinnedId) ||
                primaryTabsScreens.some((s) => s.screenId === pinnedId),
            })}
            integrations={primaryTabsApps}
            screens={primaryTabsScreens}
          />
        )}
        {secondaryTabsApps.length + secondaryTabsScreens.length > 0 && (
          <SharedTabs
            className={classNames({
              pinned:
                secondaryTabsApps.some((app) => app.id === pinnedId) ||
                secondaryTabsScreens.some((s) => s.screenId === pinnedId),
            })}
            integrations={secondaryTabsApps}
            screens={secondaryTabsScreens}
          />
        )}
        {isPinnedParticipant && (
          <ScreenPinTile
            key={pinnedId}
            className="pinned-participant"
            sessionId={pinnedId}
            style={splitAreaDimensions}
          />
        )}
        <style jsx>{`
          .screens-pins {
            align-items: center;
            background: ${colors.custom.mainAreaBg};
            display: flex;
            flex-wrap: wrap;
            gap: 1px;
            height: 100%;
            justify-content: center;
            width: 100%;
          }
          .screens-pins.portrait {
            flex-direction: column;
          }
          .screens-pins.portrait :global(> *) {
            height: 50%;
          }
          .screens-pins :global(> *) {
            flex: 1;
          }
          .screens-pins.pinned-screen :global(.shared-tabs:not(.pinned)),
          .screens-pins.pinned-integration :global(.shared-tabs:not(.pinned)) {
            flex: 0;
            overflow: hidden;
            width: 0;
          }
          .screens-pins.pinned-participant:not(.portrait)
            :global(.shared-tabs .screen-panel) {
            padding-bottom: 32px; // visually center screen share next to pinned participant
          }
          .screens-pins :global(.pinned) {
            flex: 2;
          }
          .screens-pins.pinned-participant:not(.portrait) :global(> *) {
            max-width: 50%;
          }
          .screens-pins.pinned-participant.portrait :global(> *) {
            max-width: 100%;
          }
        `}</style>
      </div>
    );
  };
